import React, { useState } from 'react';
import { FaWifi, FaSpinner, FaSync, FaPowerOff, FaEye, FaEyeSlash, FaUnlink, FaCircle } from 'react-icons/fa';
import { adminService } from '../../services/adminService';

// Customer-detail "CPE Router" section — the GUI for the GenieACS zero-touch
// provisioning pipeline (docs/GENIEACS-SETUP.md).
//
//   no serial   → assign form: type the serial off the router box at ship time
//   assigned    → shows staged/online status, Wi-Fi credentials (generated at
//                 assign time), and live ACS actions: Check status / Reboot /
//                 Resync config / Unassign
//
// "Staged" = waiting for the router's first check-in. It flips to "online"
// automatically when the router first pulls its config from the ACS.
const CPE_MODELS = ['DIR-X3000Z', 'DIR-BE3602', 'DPN-BE7212GR', 'Other'];

const timeAgo = (iso) => {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

const CpeSection = ({ customer, updateLocalCustomer }) => {
  const [serialInput, setSerialInput] = useState('');
  const [model, setModel] = useState(CPE_MODELS[0]);
  const [busy, setBusy] = useState(''); // '' | 'assign' | 'status' | 'reboot' | 'refresh' | 'unassign'
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [live, setLive] = useState(null); // last cpeDevice status result
  const [showWifiPwd, setShowWifiPwd] = useState(false);

  const assigned = !!customer?.cpeSerial;

  const run = async (label, fn) => {
    setBusy(label); setError(''); setNotice('');
    try { await fn(); }
    catch (e) { setError(e.message || 'Network error'); }
    finally { setBusy(''); }
  };

  const assign = () => run('assign', async () => {
    const serial = serialInput.trim().toUpperCase();
    if (!serial) { setError('Enter the router serial (on the sticker under the unit)'); return; }
    const res = await adminService.assignCpe({ customerId: customer.UID, serial, model });
    if (res?.cpeSerial) {
      updateLocalCustomer?.({
        ...customer,
        cpeSerial: res.cpeSerial, cpeModel: res.cpeModel, cpeStatus: res.cpeStatus,
        cpeWifiSsid: res.cpeWifiSsid, cpeWifiPassword: res.cpeWifiPassword,
      });
      setNotice('Router assigned — it will self-configure on first boot at the customer premises.');
    } else setError(res?.error || 'Assign failed');
  });

  const unassign = () => {
    if (!window.confirm(`Unassign router ${customer.cpeSerial} from this customer?`)) return;
    run('unassign', async () => {
      const res = await adminService.assignCpe({ customerId: customer.UID, unassign: true });
      if (res?.cpeStatus === 'unassigned') {
        updateLocalCustomer?.({
          ...customer,
          cpeSerial: null, cpeModel: null, cpeStatus: 'unassigned',
          cpeWifiSsid: null, cpeWifiPassword: null, cpeLastInform: null,
        });
        setLive(null);
        setNotice('Router unassigned.');
      } else setError(res?.error || 'Unassign failed');
    });
  };

  const checkStatus = () => run('status', async () => {
    const res = await adminService.cpeDevice({ customerId: customer.UID, op: 'status' });
    if (res?.serial) setLive(res);
    else setError(res?.error || 'Status check failed');
  });

  const task = (op) => {
    if (op === 'reboot' && !window.confirm('Reboot the customer\'s router? Their internet drops for ~2 minutes.')) return;
    run(op, async () => {
      const res = await adminService.cpeDevice({ customerId: customer.UID, op });
      if (res?.op) setNotice(res.message);
      else setError(res?.error || `${op} failed`);
    });
  };

  const statusDot = () => {
    if (live) {
      if (!live.acsKnown) return { color: 'text-[#928374]', label: 'Never contacted ACS' };
      return live.online
        ? { color: 'text-[#b8bb26]', label: `Online — last check-in ${timeAgo(live.lastInform)}` }
        : { color: 'text-[#fb4934]', label: `Offline — last seen ${timeAgo(live.lastInform)}` };
    }
    const s = customer?.cpeStatus;
    if (s === 'online') return { color: 'text-[#b8bb26]', label: `Online (as of ${timeAgo(customer.cpeLastInform) || 'last check-in'})` };
    if (s === 'staged') return { color: 'text-[#fabd2f]', label: 'Staged — waiting for first check-in' };
    return { color: 'text-[#928374]', label: 'Unknown' };
  };

  const dot = statusDot();

  return (
    <div className="pt-6 border-t border-[#3c3836] mt-6">
      <h3 className="text-[#ebdbb2] font-semibold mb-3 flex items-center gap-2">
        <FaWifi className="text-[#83a598]" /> CPE Router (TR-069)
      </h3>

      {!assigned ? (
        <div className="bg-[#1d2021] border border-[#3c3836] rounded-lg p-4 space-y-3">
          <p className="text-[#928374] text-sm">
            No router assigned. Type the serial from the sticker on the unit when shipping —
            the router configures itself (PPPoE, VLAN, Wi-Fi) on first boot.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              value={serialInput}
              onChange={(e) => setSerialInput(e.target.value)}
              placeholder="Router serial e.g. RTK1234567"
              className="bg-[#282828] border border-[#3c3836] rounded px-3 py-2 text-[#ebdbb2] text-sm flex-1 min-w-[220px]"
            />
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="bg-[#282828] border border-[#3c3836] rounded px-3 py-2 text-[#ebdbb2] text-sm"
            >
              {CPE_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <button
              onClick={assign}
              disabled={!!busy}
              className="bg-[#83a598] hover:bg-[#8ec07c] text-[#1d2021] font-semibold rounded px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {busy === 'assign' ? <FaSpinner className="animate-spin" /> : null} Assign router
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-[#1d2021] border border-[#3c3836] rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><span className="text-[#928374]">Serial: </span><span className="text-[#ebdbb2] font-mono">{customer.cpeSerial}</span></div>
            <div><span className="text-[#928374]">Model: </span><span className="text-[#ebdbb2]">{customer.cpeModel || '—'}</span></div>
            <div className="flex items-center gap-2">
              <FaCircle className={`${dot.color} text-[9px]`} />
              <span className="text-[#ebdbb2]">{dot.label}</span>
            </div>
            {live?.softwareVersion && (
              <div><span className="text-[#928374]">Firmware: </span><span className="text-[#ebdbb2]">{live.softwareVersion}</span></div>
            )}
            <div><span className="text-[#928374]">Wi-Fi SSID: </span><span className="text-[#ebdbb2] font-mono">{customer.cpeWifiSsid || '—'}</span></div>
            <div className="flex items-center gap-2">
              <span className="text-[#928374]">Wi-Fi password: </span>
              <span className="text-[#ebdbb2] font-mono">
                {customer.cpeWifiPassword ? (showWifiPwd ? customer.cpeWifiPassword : '••••••••••') : '—'}
              </span>
              {customer.cpeWifiPassword && (
                <button onClick={() => setShowWifiPwd(!showWifiPwd)} className="text-[#83a598] hover:text-[#8ec07c]">
                  {showWifiPwd ? <FaEyeSlash /> : <FaEye />}
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#3c3836]">
            <button onClick={checkStatus} disabled={!!busy}
              className="bg-[#83a598] hover:bg-[#8ec07c] text-[#1d2021] font-semibold rounded px-3 py-1.5 text-sm flex items-center gap-2 disabled:opacity-50">
              {busy === 'status' ? <FaSpinner className="animate-spin" /> : <FaWifi />} Check status
            </button>
            <button onClick={() => task('refresh')} disabled={!!busy}
              className="bg-[#458588] hover:bg-[#83a598] text-[#ebdbb2] rounded px-3 py-1.5 text-sm flex items-center gap-2 disabled:opacity-50">
              {busy === 'refresh' ? <FaSpinner className="animate-spin" /> : <FaSync />} Resync config
            </button>
            <button onClick={() => task('reboot')} disabled={!!busy}
              className="bg-[#d65d0e] hover:bg-[#fe8019] text-[#1d2021] font-semibold rounded px-3 py-1.5 text-sm flex items-center gap-2 disabled:opacity-50">
              {busy === 'reboot' ? <FaSpinner className="animate-spin" /> : <FaPowerOff />} Reboot
            </button>
            <button onClick={unassign} disabled={!!busy}
              className="bg-[#3c3836] hover:bg-[#504945] text-[#fb4934] rounded px-3 py-1.5 text-sm flex items-center gap-2 disabled:opacity-50 ml-auto">
              {busy === 'unassign' ? <FaSpinner className="animate-spin" /> : <FaUnlink />} Unassign
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-[#fb4934] text-sm mt-2">{error}</p>}
      {notice && <p className="text-[#b8bb26] text-sm mt-2">{notice}</p>}
    </div>
  );
};

export default CpeSection;
